import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSite } from "@/contexts/SiteContent";
import { Icon } from "@/components/Icon";
import { resolveAssetUrl, api } from "@/lib/api";
import PhotoFrame from "@/components/PhotoFrame";
import RichText from "@/components/RichText";

function CountUp({ end, suffix = "", duration = 1600 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return <span>{n.toLocaleString("en-IN")}{suffix}</span>;
}

function ctaAction(cta) {
  if (!cta) return null;
  if (cta.action === "wizard") return () => window.dispatchEvent(new CustomEvent("tara:open-wizard"));
  return null;
}

export default function HomePage() {
  const { content } = useSite();
  const hero = content?.hero || {};
  const stats = content?.stats || [];
  const marquee = content?.marquee || {};
  const why = content?.why_section || {};
  const cta = content?.cta_section || {};
  const productsSec = content?.products_section || {};
  const products = content?.products || [];
  const photos = (hero.photos || []).map(resolveAssetUrl).filter(Boolean);
  const heroImage = photos[0];

  const primary = hero.primary_cta || {};
  const secondary = hero.secondary_cta || {};
  const onPrimary = ctaAction(primary);
  const onSecondary = ctaAction(secondary);
  const ctaPhone = (cta.secondary_phone || "").replace(/\s/g, "");

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 dot-grid opacity-30 [mask-image:radial-gradient(ellipse_at_top,black,transparent_65%)]"/>
        <div className="absolute -top-32 -right-20 h-[480px] w-[480px] rounded-full bg-saffron/12 blur-3xl soft-pulse"/>
        <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-navy/12 blur-3xl soft-pulse"/>

        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 xl:px-16 pt-14 sm:pt-20 pb-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5}}>
                <div className="hero-brand-line inline-flex items-center gap-2.5 mb-4">
                  <span className="h-2 w-2 rounded-full bg-saffron"/>
                  {(() => {
                    const name = (content?.branding?.site_name || "TARA Finserv").trim();
                    const parts = name.split(/\s+/);
                    const first = parts.shift() || "";
                    const rest = parts.join(" ");
                    return (
                      <span className="text-[16px] sm:text-[18px] lg:text-[19px] font-extrabold uppercase tracking-[0.18em]">
                        <span className="text-black">{first}</span>
                        {rest && <> <span className="text-saffron">{rest}</span></>}
                      </span>
                    );
                  })()}
                  <span className="hidden sm:inline-block h-px w-12 bg-gradient-to-r from-saffron to-transparent"/>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(hero.eyebrow_chips || []).map((chip, i) => (
                    <motion.span key={i}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.06 }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 text-navy px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
                      <span className="h-1.5 w-1.5 rounded-full bg-saffron"/>{chip}
                    </motion.span>
                  ))}
                </div>
              </motion.div>

              <motion.h1
                initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{duration:0.6, delay:0.1}}
                className="font-display text-[1.85rem] sm:text-[2.5rem] lg:text-[3.1rem] xl:text-[3.5rem] font-extrabold tracking-tight leading-[1.08] mt-5 text-navy"
              >{hero.title}</motion.h1>

              <motion.div initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{duration:0.6, delay:0.2}}
                className="mt-6 max-w-2xl">
                <RichText html={hero.subtitle} variant="subtitle"/>
              </motion.div>

              <motion.div initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{duration:0.6, delay:0.3}}
                className="mt-9 flex flex-col sm:flex-row gap-3">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                  <Button data-testid="hero-apply-cta" onClick={() => window.dispatchEvent(new CustomEvent("tara:open-wizard"))} className="btn-saffron rounded-full h-13 px-7 text-base shadow-[0_14px_30px_-12px_rgba(242,117,15,0.55)]">
                    Apply Now <ArrowRight className="h-4 w-4 ml-2"/>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                  <Link to="/contact"><Button data-testid="hero-expert-cta" className="btn-navy rounded-full h-13 px-7 text-base shadow-[0_14px_30px_-12px_rgba(10,26,63,0.55)]">
                    Talk to our expert <ArrowRight className="h-4 w-4 ml-2"/>
                  </Button></Link>
                </motion.div>
              </motion.div>

              {stats.length > 0 && (
                <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6">
                  {stats.slice(0, 8).map((s, i) => (
                    <motion.div key={s.id || s.label}
                      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                      data-testid={`stat-${s.id || s.label}`} className="relative">
                      <div className="absolute -left-3 top-1.5 h-7 w-0.5 bg-gradient-to-b from-saffron to-navy rounded-full"/>
                      <div className="font-display text-3xl sm:text-4xl font-extrabold text-navy">
                        <CountUp end={Number(s.value) || 0} suffix={s.suffix || ""}/>
                      </div>
                      <div className="text-xs uppercase tracking-[0.18em] text-ink-muted mt-1">{s.label}</div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-5 relative flex items-center justify-center">
              <div className="relative w-full max-w-[460px]">
                <PhotoFrame src={heroImage} alt="hero" ratio="4/5" float testid="hero-photo"/>

                {hero.bottom_card?.title && (
                  <motion.div initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:0.4}}
                    className="absolute -bottom-6 -left-6 sm:-left-10 bg-white rounded-2xl shadow-xl p-5 border border-slate-100 max-w-[240px] z-10">
                    <div className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-saffron"/><div className="overline mb-0">{hero.bottom_card.eyebrow}</div></div>
                    <div className="font-display font-bold text-lg mt-1 text-navy">{hero.bottom_card.title}</div>
                    {hero.bottom_card.sub && <div className="text-xs text-ink-muted">{hero.bottom_card.sub}</div>}
                  </motion.div>
                )}
                {hero.top_card?.title && (
                  <motion.div initial={{opacity:0, y:-14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay:0.5}}
                    className="absolute -top-6 -right-2 bg-navy text-white rounded-2xl shadow-xl p-4 max-w-[210px] z-10">
                    <div className="text-xs uppercase tracking-[0.18em] text-saffron">{hero.top_card.eyebrow}</div>
                    <div className="font-display font-bold text-2xl mt-1">{hero.top_card.title}</div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-xs uppercase tracking-[0.22em] text-ink-muted mb-6">{marquee.heading}</div>
          <div className="marquee-wrap overflow-hidden" data-testid="partners-marquee">
            <div className="marquee">
              {[...(marquee.items || []), ...(marquee.items || [])].map((p, i) => (
                p.logo_url ? (
                  <img key={i} src={resolveAssetUrl(p.logo_url)} alt={p.name} className="h-10 w-auto opacity-60 hover:opacity-100 transition-opacity"/>
                ) : (
                  <div key={i} className="text-xl sm:text-2xl font-display font-semibold text-ink-muted hover:text-navy transition-colors whitespace-nowrap">{p.name}</div>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT BAND — admin can override every field independently via Admin → Content → About → "Home page — About band" */}
      {(() => {
        const ha = content?.home_about || {};
        const ap = content?.about_page || {};
        const apHero = ap.hero || {};
        const img = ha.image_url || apHero.image_url;
        const eyebrow = ha.eyebrow || "About TARA Finserv";
        const title = ha.title || apHero.title || "We turn loan paperwork into a 4-minute conversation.";
        const subtitle = ha.subtitle || apHero.subtitle || "TARA Finserv is a Direct Selling Agent founded in 2018, with a single mission — make credit access in India fast, fair and friendly.";
        const visionTitle = ha.vision_title || ap.vision_title || "Vision";
        const visionText  = ha.vision_text  || ap.vision_text  || "Right loan, right time, every Indian.";
        const missionTitle = ha.mission_title || ap.mission_title || "Mission";
        const missionText  = ha.mission_text  || ap.mission_text  || "Combine deep lender relationships with transparency and modern technology.";
        const ctaLabel = ha.cta_label || "Learn more about us";
        return (
          <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 xl:gap-24 items-center">
              <div className="lg:col-span-5 lg:pr-4">
                <PhotoFrame src={img} alt="About TARA" ratio="4/5" testid="home-about-photo"/>
              </div>
              <motion.div initial={{opacity:0, x:30}} whileInView={{opacity:1, x:0}} viewport={{once:true}} transition={{duration:0.7}} className="lg:col-span-7 lg:pl-4">
                <span className="overline">{eyebrow}</span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-4 text-navy">{title}</h2>
                <RichText html={subtitle} variant="subtitle" className="mt-5"/>
                <div className="mt-6 grid grid-cols-2 gap-5">
                  <div>
                    <div className="overline">{visionTitle}</div>
                    <RichText html={visionText} variant="snippet" className="mt-2"/>
                  </div>
                  <div>
                    <div className="overline">{missionTitle}</div>
                    <RichText html={missionText} variant="snippet" className="mt-2"/>
                  </div>
                </div>
                <Link to="/about"><Button data-testid="home-about-cta" className="btn-outline-navy rounded-full h-12 px-7 mt-8">{ctaLabel} <ArrowRight className="h-4 w-4 ml-2"/></Button></Link>
              </motion.div>
            </div>
          </section>
        );
      })()}

      {/* PRODUCTS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="eyebrow-bar"><span className="overline">{productsSec.eyebrow || "Products"}</span></span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 max-w-2xl text-navy">{productsSec.heading}</h2>
          </div>
          <Link to="/products" className="text-sm font-semibold underline-anim text-navy hover:text-saffron inline-flex items-center gap-1.5" data-testid="see-all-products">
            {productsSec.see_all_label || "See all"} <ArrowRight className="h-4 w-4"/>
          </Link>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p, idx) => (
            <motion.div key={p.slug}
              initial={{opacity:0, y:18}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.45, delay: idx*0.05}}>
              <Link to={`/p/${p.slug}`} data-testid={`product-${p.slug}`} className="card-flat p-6 sm:p-7 h-full flex flex-col gap-4 group">
                <div className="relative h-12 w-12 grid place-items-center rounded-2xl bg-saffron text-white shadow-[0_10px_22px_-10px_rgba(242,117,15,0.6)]">
                  <Icon name={p.icon} className="h-5 w-5" strokeWidth={1.8}/>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-navy">{p.title}</div>
                  <div className="text-sm text-ink-muted mt-1">{p.blurb}</div>
                </div>
                <div className="mt-auto text-sm font-semibold text-saffron inline-flex items-center gap-1.5">
                  Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform"/>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY */}
      {(why.items || []).length > 0 && (
        <section className="bg-navy text-white relative overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-10"/>
          <div className="absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-saffron/20 blur-3xl"/>
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <span className="eyebrow-bar"><span className="overline text-saffron">{why.eyebrow}</span></span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4">{why.heading}</h2>
                <RichText html={why.blurb} variant="navy" className="mt-5"/>
                {why.cta_label && (
                  <Button data-testid="why-cta" onClick={() => window.dispatchEvent(new CustomEvent("tara:open-wizard"))} className="btn-saffron rounded-full h-12 px-7 mt-8">
                    {why.cta_label} <ArrowRight className="h-4 w-4 ml-2"/>
                  </Button>
                )}
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
                {why.items.map((w, i) => (
                  <motion.div key={i} initial={{opacity:0, y:18}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.45, delay: i*0.07}}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-saffron text-white">
                      <Icon name={w.icon} className="h-5 w-5" strokeWidth={1.8}/>
                    </div>
                    <h3 className="font-display text-xl font-bold mt-4">{w.title}</h3>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 p-8 sm:p-14">
          <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-saffron/20 blur-3xl"/>
          <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-navy/15 blur-3xl"/>
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="eyebrow-bar"><span className="overline">{cta.eyebrow}</span></span>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-4 text-navy">{cta.heading}</h3>
              <RichText html={cta.subtitle} variant="body" className="mt-4"/>
            </div>
            <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
              <Button data-testid="cta-apply" onClick={() => window.dispatchEvent(new CustomEvent("tara:open-wizard"))} className="btn-saffron rounded-full h-12 px-7">
                {cta.primary_label || "Start application"}
              </Button>
              {cta.secondary_label && (
                <a href={`tel:${ctaPhone}`}>
                  <Button data-testid="cta-call-advisor" className="btn-outline-navy rounded-full h-12 px-7 w-full">{cta.secondary_label}</Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
